import type { RuleSet } from '../../../rules';
import {
  type Building,
  buildingLevel,
  type EntityId,
  isCity,
  levelDef,
  levelTitle,
  type PlayerId,
  type SimContext,
  type SimState,
  toMilli,
} from '../../../sim';
import { cityName } from '../../cities';
import type { Names } from '../../names';
import { levelPreview, type UpgradePreview } from '../../upgradePreview';
import { clockText, upgradeProgress } from '../../upgradeProgress';
import type { StateSummary } from '../summary';
import type { GameEvent } from '../types';

/**
 * Upgrade alerts for the viewer's own buildings. `upgradePaused` is a
 * condition (keyed per building, held while an upgrade in progress is stalled
 * by attacks); `upgraded` and `era` are events raised when a building's
 * level rises between two frames (a city's is an era). Upgrades in progress
 * are the owner's secret, so only own buildings are read. What a level
 * changes and unlocks comes from `levelPreview`, the data of the Upgrades view.
 */

/** The condition key of building `id`'s paused upgrade. */
export const upgradePausedKey = (id: EntityId) => `upgrade-paused:${id}`;

/** Stat rows listed in an upgraded building's detail. */
const MAX_ROWS = 3;
/** Unlocks named in a detail line; the rest read "and N more". */
export const MAX_UNLOCKS = 4;

/** "Level 2", or its title where the rules give one ("Mechanised"). */
function levelLabel(rules: RuleSet, b: Pick<Building, 'type'>, level: number): string {
  const def = rules.buildings[b.type];
  const title = def ? levelTitle(def, level) : undefined;
  return title && title !== def?.name ? title : `Level ${level}`;
}

/** Name of the building in alerts: a city by its number or name, else its type. */
function buildingLabel(
  rules: RuleSet,
  b: Building,
  playerId: PlayerId,
  cityNumbers: ReadonlyMap<string, number>,
  names?: Names
): string {
  return isCity(b, rules)
    ? cityName(b, playerId, cityNumbers, undefined, names)
    : (rules.buildings[b.type]?.name ?? b.type);
}

/**
 * One condition per own building whose upgrade is held by attacks: target
 * level, progress ("0:16 of 0:25") and the rule that it resumes by itself.
 */
export function upgradePausedAlerts(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  cityNumbers: ReadonlyMap<string, number>,
  names?: Names
): GameEvent[] {
  const events: GameEvent[] = [];
  for (const b of Object.values(state.buildings)) {
    if (b.owner !== playerId || !b.upgrading) continue;
    const up = upgradeProgress(state, rules, b, playerId);
    const def = rules.buildings[b.type];
    const target = def && levelDef(def, b.upgrading.level);
    if (!up?.paused || !def || !target) continue;
    const total = toMilli(target.upgradeTime) / toMilli(1);
    const label = buildingLabel(rules, b, playerId, cityNumbers, names);
    events.push({
      kind: 'upgradePaused',
      key: upgradePausedKey(b.id),
      text: `Upgrade paused: ${label} under attack`,
      detail:
        `${def.name} → ${levelLabel(rules, b, up.level)} stopped at ` +
        `${clockText(total - up.secondsLeft)} of ${clockText(total)}. ` +
        'It resumes when the attacks stop. Nothing is lost.',
      progress: up.fraction,
      pos: { ...b.pos },
      ...(isCity(b, rules) ? { cityId: b.id } : {}),
    });
  }
  return events;
}

/** "Mine, Tank and 2 more": the first MAX_UNLOCKS names. */
function unlockList(names: readonly string[]): string {
  const more = names.length - MAX_UNLOCKS;
  return more > 0 ? `${names.slice(0, MAX_UNLOCKS).join(', ')} and ${more} more` : names.join(', ');
}

/** "Territory radius 3 → 4, Max hp 800 → 1000. Unlocks Mine, Tank." */
function levelDetail(preview: UpgradePreview | null): string | undefined {
  if (!preview) return undefined;
  const rows = preview.rows.slice(0, MAX_ROWS).map((r) => `${r.label} ${r.before} → ${r.after}`);
  const parts = [
    rows.length > 0 ? `${rows.join(', ')}.` : '',
    preview.enables.length > 0 ? `Unlocks ${unlockList(preview.enables)}.` : '',
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : undefined;
}

/**
 * Own completed buildings whose level rose since `prev`: "Farm reached level
 * 2" (a title of the rules in brackets), or for a city "City 1 is now a
 * Stronghold" with what the level changes and unlocks. A building new to the
 * summary (built, or seen at a higher level) is not an upgrade. The detail
 * reads the preview of the step just taken, from the state with the building
 * back at its old level.
 */
export function levelEvents(
  prev: StateSummary,
  next: StateSummary,
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  cityNumbers: ReadonlyMap<string, number>,
  names?: Names
): GameEvent[] {
  const { rules } = ctx;
  const events: GameEvent[] = [];
  for (const [id, now] of next.buildings) {
    const before = prev.buildings.get(id);
    const b = state.buildings[id];
    if (!before || !b || now.owner !== playerId || !before.done || !now.done) continue;
    if (before.level === undefined || now.level === undefined || now.level <= before.level)
      continue;
    const def = rules.buildings[b.type];
    if (!def) continue;
    const level = buildingLevel(b);
    const old = { ...b, level: level - 1 };
    const preview = levelPreview(
      { ...state, buildings: { ...state.buildings, [id]: old } },
      ctx,
      playerId,
      old,
      cityNumbers,
      names
    );
    const title = levelTitle(def, level);
    if (isCity(b, rules)) {
      const label = buildingLabel(rules, b, playerId, cityNumbers, names);
      events.push({
        kind: 'era',
        text: title !== def.name ? `${label} is now a ${title}` : `${label} reached level ${level}`,
        detail: levelDetail(preview),
        pos: { ...b.pos },
        cityId: id,
      });
      continue;
    }
    events.push({
      kind: 'upgraded',
      text: `${def.name} reached level ${level}${title !== def.name ? ` (${title})` : ''}`,
      detail: levelDetail(preview),
      pos: { ...b.pos },
    });
  }
  return events;
}
